import {
  DEFAULT_GMAIL_TRIGGER_LABELS,
  type GmailTriggerDraft,
  type GmailTriggerLabel,
} from '@littlebird/sdk';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';

import { RoutineGmailQuery } from './RoutineGmailQuery';

/** Just the fields the trigger row needs, so the editor isn't coupled to the integrations schema. */
export type GmailAccountOption = { id: string; account_label: string };

const ANY_MAILBOX = 'any';

const MAIL_DIRECTION_OPTIONS: { label: string; labelId: GmailTriggerLabel }[] =
  [
    { label: 'Received', labelId: 'INBOX' },
    { label: 'Sent', labelId: 'SENT' },
  ];

export const RoutineGmailTriggerRow = ({
  trigger,
  gmailAccounts,
  onAccountChange,
  onDirectionChange,
  onConfigure,
  configurationDisabled,
  editingDisabled,
  onRemove,
}: {
  trigger: GmailTriggerDraft;
  gmailAccounts: GmailAccountOption[];
  onAccountChange: (accountId: string | null) => void;
  onDirectionChange: (labelIds: GmailTriggerLabel[]) => void;
  onConfigure: () => void;
  configurationDisabled: boolean;
  editingDisabled: boolean;
  onRemove: () => void;
}) => {
  const direction = trigger.labelIds.includes('SENT')
    ? trigger.labelIds.includes('INBOX')
      ? 'received or sent'
      : 'sent'
    : 'received';
  const mailbox = trigger.connectedAccountId
    ? gmailAccounts.find((account) => account.id === trigger.connectedAccountId)
        ?.account_label || 'Connected Gmail account'
    : 'Any connected Gmail account';

  return (
    <YStack
      role="group"
      aria-label="Email trigger"
      className="min-w-0 gap-3 rounded-xl border border-border bg-surface px-3 py-3"
    >
      <XStack className="items-start justify-between gap-2">
        <XStack className="min-w-0 flex-1 flex-wrap items-start gap-2">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-surface">
            <Icons.gmail className="size-4" />
          </span>
          <XStack className="min-w-0 flex-1 basis-96 flex-wrap items-baseline gap-2 text-sm">
            <span>New</span>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button
                  aria-label={`Mail: ${direction}`}
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1.5 px-2"
                  disabled={editingDisabled}
                >
                  {direction}
                  <Icons.chevronDown className="size-3.5 shrink-0" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="start" className="w-40">
                {MAIL_DIRECTION_OPTIONS.map((option) => (
                  <DropdownMenu.CheckboxItem
                    key={option.labelId}
                    checked={trigger.labelIds.includes(option.labelId)}
                    disabled={
                      trigger.labelIds.length === 1 &&
                      trigger.labelIds.includes(option.labelId)
                    }
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={(checked) =>
                      onDirectionChange(
                        DEFAULT_GMAIL_TRIGGER_LABELS.filter((label) =>
                          label === option.labelId
                            ? checked === true
                            : trigger.labelIds.includes(label)
                        )
                      )
                    }
                  >
                    {option.label}
                  </DropdownMenu.CheckboxItem>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Root>
            <span>email</span>
            <span className="text-foreground-muted">in</span>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button
                  aria-label={`Mailbox: ${mailbox}`}
                  variant="outline"
                  size="sm"
                  className="min-w-0 max-w-full gap-1.5 px-2"
                  disabled={editingDisabled || gmailAccounts.length === 0}
                >
                  <span className="truncate">{mailbox}</span>
                  <Icons.chevronDown className="size-3.5 shrink-0" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="start" className="w-64">
                <DropdownMenu.RadioGroup
                  value={trigger.connectedAccountId ?? ANY_MAILBOX}
                  onValueChange={(value) =>
                    onAccountChange(value === ANY_MAILBOX ? null : value)
                  }
                >
                  <DropdownMenu.RadioItem value={ANY_MAILBOX}>
                    Any connected Gmail account
                  </DropdownMenu.RadioItem>
                  {gmailAccounts.map((account) => (
                    <DropdownMenu.RadioItem key={account.id} value={account.id}>
                      {account.account_label || 'Connected Gmail account'}
                    </DropdownMenu.RadioItem>
                  ))}
                </DropdownMenu.RadioGroup>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
            <RoutineGmailQuery query={trigger.query} />
          </XStack>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={configurationDisabled}
            onClick={onConfigure}
          >
            <Icons.sparkle className="size-3.5" />
            Configure with Littlebird
          </Button>
        </XStack>
        <Button
          aria-label="Remove email trigger"
          variant="ghost"
          size="icon-sm"
          iconOnly
          className="mt-1 shrink-0 text-foreground-muted"
          onClick={onRemove}
          disabled={editingDisabled}
        >
          <Icons.trash2 />
        </Button>
      </XStack>
    </YStack>
  );
};
